// src/v2/pulse/SpaceHeadroomV2.jsx
//
// SPACE-PIPELINE-S1 — "Space pipeline" web part, v2 Pulse page Section 6.
// Replaces the former KPI-strip / Capacity-pressure-outlook-tabs /
// Markets-to-watch layout with 3 bucketed columns built from live
// GET /api/plan data (see spacePipeline.js).
//
// SPACE-PIPELINE-PANE-PUNCHLIST: each column truncates its row list to 5
// with a "View all (N) →" expand-in-place toggle; the Seats available
// column shows two distinct labeled figures per row/rollup ("Available
// now" and "Headroom") instead of one conflated number; the header status
// badge, footer insight line, and footer "Pin signal" button were removed;
// hub (◆) markers now render in one consistent color across every column.
//
// SPACE-PIPELINE-PANE-REFINE-2: row limit lowered to 3; overflow toggle
// replaced with "more ▾"/"less ▾" matching WhatIfPane style; "Open in
// Plan →" card footer removed; per-row deep-link added; What-If scroll
// fix in App.jsx.
//
// WHATIF-SINGLE-SURFACE-LINK-TO-MARKETPLAN: What-If lives only on Market
// Plan now. The space scenario footer link deep-links straight into Market
// Plan's own What-If view via onNavigate.
//
// SPACE-PIPELINE-REMOVE-ROW-EXPAND: expand card removed; row click navigates
// directly to the Plan destination (same target as the → arrow).
//
// Props:
//   markets    — markets[] from getPlan() (marketsClient.js), passed down
//                from PulsePage. Defaults to [] so the card renders
//                empty-state columns rather than throwing when data hasn't
//                loaded yet.
//   onNavigate — callback used to deep-link the What-If footer link into
//                Market Plan's What-If view, and for per-row plan links.

import { useState, useRef } from "react";
import { PaneInfoTooltip } from "./PaneInfoTooltip.jsx";
import { T } from "../../app/tokens.js";
import { PULSE_PANE_H, OUTBOUND_LINK_STYLE } from "./pulseConstants.js";
import { buildSpacePipelineColumns } from "./spacePipeline.js";
import { useCountUp } from "../hooks/useCountUp.js";

/* ─── Constants ──────────────────────────────────────────────────────────── */

const SH_TOOLTIP = "Seats coming online from new build-outs, open seats in operational offices, and offices at or over practical capacity - sourced live from the portfolio plan.";

const COLUMN_META = [
  { key: "seatsAvailable", dot: "var(--v2-green)",  label: "Seats available today", testId: "seats-available" },
  { key: "needsSeats",     dot: "var(--v2-red)",    label: "Needs seats today",     testId: "needs-seats"     },
  { key: "comingOnline",   dot: "var(--v2-accent)", label: "Coming online",         testId: "coming-online"   },
];

// Rows beyond this count are collapsed behind a "more ▾" / "less ▾" toggle.
const VISIBLE_ROW_LIMIT = 3;

// Hub (◆) markers render in one consistent color across every column instead
// of being tinted to match each column's accent color.
const HUB_MARKER_COLOR = "var(--v2-dark)";

/* ─── Helpers ────────────────────────────────────────────────────────────── */

function formatMetric(metric, unit) {
  if (unit === "open") return `+${metric}`;
  return String(metric);
}

/* ─── Sub-components ─────────────────────────────────────────────────────── */

// SPACE-PIPELINE-ALIGN-HEADER + MERGE-COMING-ONLINE-DROPDOWN: row renderer
// for Coming online rows that have offsetting exits — shows the signed net.
// Row click and → arrow both navigate directly to the Plan destination.
function ComingOnlineRow({ row, meta, onRowNavigate, planEnabled }) {
  // SPACE-PIPELINE-PANE-REFINE-2: Coming online rows are market-level
  // aggregates (key = market.id). For deep-link navigation we use
  // primaryLocationId (first build component's officeId) so MarketPlanPage
  // can find and expand the parent market row for a real location.
  const navLocationId = row.primaryLocationId ?? row.key;
  const navTarget = `?view=plan&location=${encodeURIComponent(navLocationId)}&plan=1`;

  return (
    <div
      data-testid={`v2-pipeline-row-${meta.testId}`}
      style={{
        display: "flex", justifyContent: "space-between", alignItems: "flex-start",
        padding: "5px 0", borderTop: "1px solid var(--v2-hair)",
        cursor: planEnabled ? "pointer" : "default",
      }}
      onClick={planEnabled ? () => onRowNavigate?.(navTarget) : undefined}
      role={planEnabled ? "button" : undefined}
      tabIndex={planEnabled ? 0 : undefined}
      onKeyDown={planEnabled ? (e) => { if (e.key === "Enter" || e.key === " ") onRowNavigate?.(navTarget); } : undefined}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", minWidth: 0, fontSize: T.fsBase, fontWeight: T.fwBold, color: "var(--v2-dark)" }}>
          <span
            title={`${row.marketName} · ${row.officeName}`}
            style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}
          >
            {row.marketName} · {row.officeName}
          </span>
          {row.isHub && <span data-testid={`v2-pipeline-hub-${row.key}`} title="Hub location" style={{ color: HUB_MARKER_COLOR, marginLeft: 4, flexShrink: 0 }}>◆</span>}
        </div>
        <div style={{ fontSize: T.fsXs, color: "var(--v2-mid)", marginTop: 1 }}>
          {(row.components?.length ?? 0)} office{(row.components?.length ?? 0) === 1 ? "" : "s"}
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8, flexShrink: 0, marginLeft: 10 }}>
        <div style={{ textAlign: "right", minWidth: 36 }}>
          <span style={{ fontSize: T.fsBase, fontWeight: T.fwDisplay, color: "var(--v2-accent)" }}>
            {row.metric >= 0 ? `+${row.metric}` : `${row.metric}`}
          </span>
        </div>
        {planEnabled && <button
          data-testid={`v2-pipeline-row-nav-${row.key}`}
          onClick={(e) => { e.stopPropagation(); onRowNavigate?.(navTarget); }}
          style={{ background: "none", border: "none", cursor: "pointer", fontSize: T.fsXs, color: "var(--v2-accent)", padding: 0, fontWeight: T.fwBold, marginTop: 2 }}
          aria-label={`Open ${row.marketName} in Plan`}
        >
          →
        </button>}
      </div>
    </div>
  );
}

// Row renderer for the two single-metric columns (Needs seats and Coming online
// single-office rows). Row click and → arrow both navigate directly to Plan.
function StandardRow({ row, meta, unit, onRowNavigate, planEnabled }) {
  // PULSE-SPACE-PIPELINE-DEFICIT-BY-OFFICE: needsSeats metric is now a
  // positive deficit count (headcount − installedSeats when > 0). Render red
  // when metric > 0 (there is a real deficit); 0 stays neutral.
  const tinted = meta.key === "comingOnline"
    ? "var(--v2-accent)"
    : (meta.key === "needsSeats" && row.metric > 0 ? "var(--v2-red)" : "var(--v2-dark)");
  // SPACE-PIPELINE-PANE-REFINE-2: Coming online rows are market-level
  // aggregates. Use primaryLocationId (first build component's officeId) so
  // the deep-link targets a real location entry in MarketPlanPage.
  const navLocationId = (meta.key === "comingOnline" && row.primaryLocationId)
    ? row.primaryLocationId
    : row.key;
  const navTarget = `?view=plan&location=${encodeURIComponent(navLocationId)}&plan=1`;

  return (
    <div
      data-testid={`v2-pipeline-row-${meta.testId}`}
      style={{
        display: "flex", justifyContent: "space-between", alignItems: "flex-start",
        padding: "5px 0", borderTop: "1px solid var(--v2-hair)",
        cursor: planEnabled ? "pointer" : "default",
      }}
      onClick={planEnabled ? () => onRowNavigate?.(navTarget) : undefined}
      role={planEnabled ? "button" : undefined}
      tabIndex={planEnabled ? 0 : undefined}
      onKeyDown={planEnabled ? (e) => { if (e.key === "Enter" || e.key === " ") onRowNavigate?.(navTarget); } : undefined}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", minWidth: 0, fontSize: T.fsBase, fontWeight: T.fwBold, color: "var(--v2-dark)" }}>
          <span
            title={`${row.marketName} · ${row.officeName}`}
            style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}
          >
            {row.marketName} · {row.officeName}
          </span>
          {row.isHub && <span data-testid={`v2-pipeline-hub-${row.key}`} title="Hub location" style={{ color: HUB_MARKER_COLOR, marginLeft: 4, flexShrink: 0 }}>◆</span>}
        </div>
        <div style={{ fontSize: T.fsXs, color: "var(--v2-mid)", marginTop: 1 }}>{row.status}</div>
      </div>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8, flexShrink: 0, marginLeft: 10 }}>
        <div style={{ textAlign: "right", minWidth: 36 }}>
          <span data-testid={`v2-pipeline-row-metric-${row.key}`} style={{ fontSize: T.fsBase, fontWeight: T.fwDisplay, color: tinted }}>
            {meta.key === "comingOnline"
              ? `+${formatMetric(row.metric, unit)}`
              : meta.key === "needsSeats" && row.metric > 0
              ? `\u2212${formatMetric(row.metric, unit)}`
              : formatMetric(row.metric, unit)}
          </span>
        </div>
        {planEnabled && <button
          data-testid={`v2-pipeline-row-nav-${row.key}`}
          onClick={(e) => { e.stopPropagation(); onRowNavigate?.(navTarget); }}
          style={{ background: "none", border: "none", cursor: "pointer", fontSize: T.fsXs, color: "var(--v2-accent)", padding: 0, fontWeight: T.fwBold, marginTop: 2 }}
          aria-label={`Open ${row.marketName} in Plan`}
        >
          →
        </button>}
      </div>
    </div>
  );
}

// Needs seats rows show the current seat deficit alongside in-place density
// expansion capacity. Row click and → arrow retain the standard Plan deep-link.
function NeedsSeatsRow({ row, meta, onRowNavigate, planEnabled }) {
  const navTarget = `?view=plan&location=${encodeURIComponent(row.key)}&plan=1`;

  return (
    <div
      data-testid={`v2-pipeline-row-${meta.testId}`}
      style={{
        display: "flex", justifyContent: "space-between", alignItems: "flex-start",
        padding: "5px 0", borderTop: "1px solid var(--v2-hair)",
        cursor: planEnabled ? "pointer" : "default",
      }}
      onClick={planEnabled ? () => onRowNavigate?.(navTarget) : undefined}
      role={planEnabled ? "button" : undefined}
      tabIndex={planEnabled ? 0 : undefined}
      onKeyDown={planEnabled ? (e) => { if (e.key === "Enter" || e.key === " ") onRowNavigate?.(navTarget); } : undefined}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", minWidth: 0, fontSize: T.fsBase, fontWeight: T.fwBold, color: "var(--v2-dark)" }}>
          <span
            title={`${row.marketName} · ${row.officeName}`}
            style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}
          >
            {row.marketName} · {row.officeName}
          </span>
          {row.isHub && <span data-testid={`v2-pipeline-hub-${row.key}`} title="Hub location" style={{ color: HUB_MARKER_COLOR, marginLeft: 4, flexShrink: 0 }}>◆</span>}
        </div>
        <div style={{ fontSize: T.fsXs, color: "var(--v2-mid)", marginTop: 1 }}>{row.status}</div>
      </div>
      <div style={{ display: "flex", gap: 8, flexShrink: 0, marginLeft: 10, alignItems: "flex-start" }}>
        <div style={{ display: "flex", gap: 12 }}>
          <div style={{ textAlign: "right", minWidth: 36 }}>
            <span data-testid={`v2-pipeline-row-metric-${row.key}`} style={{ fontSize: T.fsBase, fontWeight: T.fwDisplay, color: "var(--v2-red)" }}>
              −{row.metric}
            </span>
          </div>
          <div data-testid={`v2-pipeline-needs-expand-${row.key}`} style={{ textAlign: "right", minWidth: 36 }}>
            <span style={{ fontSize: T.fsBase, fontWeight: T.fwDisplay, color: "var(--v2-mid)" }}>
              {row.expansion > 0 ? `+${row.expansion}` : "0"}
            </span>
          </div>
        </div>
        {planEnabled && <button
          data-testid={`v2-pipeline-row-nav-${row.key}`}
          onClick={(e) => { e.stopPropagation(); onRowNavigate?.(navTarget); }}
          style={{ background: "none", border: "none", cursor: "pointer", fontSize: T.fsXs, color: "var(--v2-accent)", padding: 0, fontWeight: T.fwBold, marginTop: 2 }}
          aria-label={`Open ${row.marketName} in Plan`}
        >
          →
        </button>}
      </div>
    </div>
  );
}

// Row renderer for the Seats available column — shows "Available now" and
// "Headroom" side by side. Row click and → arrow both navigate directly to Plan.
function SeatsAvailableRow({ row, meta, onRowNavigate, planEnabled }) {
  const navTarget = `?view=plan&location=${encodeURIComponent(row.key)}&plan=1`;

  return (
    <div
      data-testid={`v2-pipeline-row-${meta.testId}`}
      style={{
        display: "flex", justifyContent: "space-between", alignItems: "flex-start",
        padding: "5px 0", borderTop: "1px solid var(--v2-hair)",
        cursor: planEnabled ? "pointer" : "default",
      }}
      onClick={planEnabled ? () => onRowNavigate?.(navTarget) : undefined}
      role={planEnabled ? "button" : undefined}
      tabIndex={planEnabled ? 0 : undefined}
      onKeyDown={planEnabled ? (e) => { if (e.key === "Enter" || e.key === " ") onRowNavigate?.(navTarget); } : undefined}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", minWidth: 0, fontSize: T.fsBase, fontWeight: T.fwBold, color: "var(--v2-dark)" }}>
          <span
            title={`${row.marketName} · ${row.officeName}`}
            style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}
          >
            {row.marketName} · {row.officeName}
          </span>
          {row.isHub && <span data-testid={`v2-pipeline-hub-${row.key}`} title="Hub location" style={{ color: HUB_MARKER_COLOR, marginLeft: 4, flexShrink: 0 }}>◆</span>}
        </div>
        <div style={{ fontSize: T.fsXs, color: "var(--v2-mid)", marginTop: 1 }}>{row.status}</div>
      </div>
      <div style={{ display: "flex", gap: 8, flexShrink: 0, marginLeft: 10, alignItems: "flex-start" }}>
        <div style={{ display: "flex", gap: 12 }}>
          <div data-testid="v2-pipeline-available-now" style={{ textAlign: "right", minWidth: 36 }}>
            <span style={{ fontSize: T.fsBase, fontWeight: T.fwDisplay, color: "var(--v2-green)" }}>
              {row.availableNow == null ? "—" : `+${row.availableNow}`}
            </span>
          </div>
          <div data-testid="v2-pipeline-headroom" style={{ textAlign: "right", minWidth: 36 }}>
            <span style={{ fontSize: T.fsBase, fontWeight: T.fwDisplay, color: "var(--v2-mid)" }}>
              {row.headroom == null ? "—" : row.headroom > 0 ? `+${row.headroom}` : row.headroom < 0 ? `\u2212${Math.abs(row.headroom)}` : "0"}
            </span>
          </div>
        </div>
        {planEnabled && <button
          data-testid={`v2-pipeline-row-nav-${row.key}`}
          onClick={(e) => { e.stopPropagation(); onRowNavigate?.(navTarget); }}
          style={{ background: "none", border: "none", cursor: "pointer", fontSize: T.fsXs, color: "var(--v2-accent)", padding: 0, fontWeight: T.fwBold, marginTop: 2 }}
          aria-label={`Open ${row.marketName} in Plan`}
        >
          →
        </button>}
      </div>
    </div>
  );
}

function PipelineColumn({ meta, column, onRowNavigate, planEnabled }) {
  const [expanded, setExpanded] = useState(false);
  const isSeatsAvailable = meta.key === "seatsAvailable";
  const visibleRows = expanded ? column.rows : column.rows.slice(0, VISIBLE_ROW_LIMIT);
  const hiddenCount = column.rows.length - VISIBLE_ROW_LIMIT;

  // Stable mount key — initialised once so the count-up fires on mount only,
  // not on re-renders caused by row expand / filter changes.
  const mountKey = useRef(`pipeline-${meta.key}`);

  const COUNT_UP_OPTS = { from: 0, duration: 1000 };

  // Four animated header numbers. For the negative Gap (needsSeats) we animate
  // the magnitude (always ≥ 0) and prepend the − sign in JSX so sign + color
  // stay rock-steady throughout the animation.
  const animAvailableNow = useCountUp(column.rollupAvailableNow ?? 0, mountKey.current, { ...COUNT_UP_OPTS, index: 0 });
  const animHeadroom     = useCountUp(column.rollupHeadroom     ?? 0, mountKey.current, { ...COUNT_UP_OPTS, index: 1 });
  const animNeedsSeats   = useCountUp(Math.abs(column.rollup    ?? 0), mountKey.current, { ...COUNT_UP_OPTS, index: 0 });
  const animComingOnline = useCountUp(column.rollup             ?? 0, mountKey.current, { ...COUNT_UP_OPTS, index: 0 });

  return (
    <div
      data-testid={`v2-pipeline-col-${meta.testId}`}
      style={{ border: "1px solid var(--v2-line)", borderRadius: 14, padding: "10px 14px", display: "flex", flexDirection: "column" }}
    >
      {/* SPACE-PIPELINE-ALIGN-HEADER: shared fixed-height header — label row,
          number row, unit line, and description each get a fixed-height
          slot so the divider below lands at the exact same height across
          all three columns, regardless of whether the number row holds one
          number, a labeled pair, or the description wraps to 1 or 2 lines. */}
      {/* SPACE-PIPELINE-DENSIFY: line 1 — dot + label + market count inline */}
      <div style={{ minHeight: 20, display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ width: 8, height: 8, borderRadius: "50%", background: meta.dot, flexShrink: 0 }} />
        <span style={{ fontSize: T.fsXs, fontWeight: T.fwBold, letterSpacing: ".05em", textTransform: "uppercase", color: T.mid }}>
          {meta.label}
        </span>
        <span style={{ fontSize: T.fsXs, color: "var(--v2-light)" }}>
          · {column.marketCount} market{column.marketCount === 1 ? "" : "s"}
        </span>
      </div>

      {/* SPACE-PIPELINE-DENSIFY: line 2 — rollup total(s) inline with unit label */}
      <div style={{ minHeight: 28, display: "flex", alignItems: "baseline", flexWrap: "wrap", gap: 8, marginTop: 2, minWidth: 0, overflow: "hidden" }}>
        {isSeatsAvailable ? (
          <>
            <span style={{ display: "inline-flex", alignItems: "baseline", gap: 4, flexShrink: 0 }}>
              <span style={{ fontSize: T.fsXl, fontWeight: T.fwDisplay, color: "var(--v2-green)", lineHeight: 1 }}>
                +{animAvailableNow}
              </span>
              <span style={{ fontSize: T.fsXs, color: "var(--v2-mid)" }}>available</span>
            </span>
            <span style={{ display: "inline-flex", alignItems: "baseline", gap: 4, flexShrink: 0 }}>
              <span style={{ fontSize: T.fsXl, fontWeight: T.fwDisplay, color: "var(--v2-mid)", lineHeight: 1 }}>
                +{animHeadroom}
              </span>
              <span style={{ fontSize: T.fsXs, color: "var(--v2-mid)" }}>expansion</span>
            </span>
          </>
        ) : (
          <>
            <div
              data-testid={meta.key === "needsSeats" ? "v2-pipeline-rollup-needs-seats" : undefined}
              style={{
                fontSize: T.fsXl, fontWeight: T.fwDisplay, lineHeight: 1,
                color: meta.key === "needsSeats" && column.rollup > 0
                  ? "var(--v2-red)"
                  : meta.key === "comingOnline"
                  ? "var(--v2-accent)"
                  : "var(--v2-dark)",
              }}
            >
              {meta.key === "needsSeats"
                ? (column.rollup > 0 ? `\u2212${animNeedsSeats}` : animNeedsSeats)
                : meta.key === "comingOnline"
                ? `+${animComingOnline}`
                : column.rollup}
            </div>
            <span style={{ fontSize: T.fsXs, color: "var(--v2-mid)" }}>{column.unit}</span>
          </>
        )}
      </div>

      {/* SPACE-PIPELINE-DENSIFY: line 3 — description clamped to 2 lines */}
      <div
        style={{
          minHeight: 32, fontSize: T.fsXs, color: "var(--v2-light)", marginTop: 1,
          overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
        }}
      >
        {column.subLabel}
      </div>
      <div style={{ borderBottom: "1px solid var(--v2-line)", marginTop: 6, marginBottom: 2 }} />

      {column.rows.length === 0 ? (
        <div
          data-testid={`v2-pipeline-empty-${meta.testId}`}
          style={{
            flex: 1, display: "grid", placeItems: "center", minHeight: 60,
            border: "1.5px dashed var(--v2-line)", borderRadius: 10,
            color: "var(--v2-light)", fontSize: T.fsBase, fontStyle: "italic",
          }}
        >
          No markets right now
        </div>
      ) : (
        <div style={{ flex: 1 }}>
          {/* SPACE-PIPELINE-DENSIFY: mini column-header row — labels stated once per section */}
          <div
            data-testid={`v2-pipeline-col-header-${meta.testId}`}
            style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "2px 0 2px" }}
          >
            <span style={{ fontSize: T.fsXs, fontWeight: T.fwBold, textTransform: "uppercase", color: T.mid, letterSpacing: ".04em", minWidth: 0 }}>
              Office
            </span>
            <div style={{ display: "flex", gap: 8, flexShrink: 0, marginLeft: 10 }}>
              {isSeatsAvailable ? (
                <>
                  <span style={{ fontSize: T.fsXs, fontWeight: T.fwBold, textTransform: "uppercase", color: T.mid, letterSpacing: ".04em", minWidth: 36, textAlign: "right" }}>Avail</span>
                  <span style={{ fontSize: T.fsXs, fontWeight: T.fwBold, textTransform: "uppercase", color: T.mid, letterSpacing: ".04em", minWidth: 36, textAlign: "right" }}>Expand</span>
                </>
              ) : meta.key === "needsSeats" ? (
                <>
                  <span style={{ fontSize: T.fsXs, fontWeight: T.fwBold, textTransform: "uppercase", color: T.mid, letterSpacing: ".04em", minWidth: 36, textAlign: "right" }}>Gap</span>
                  <span style={{ fontSize: T.fsXs, fontWeight: T.fwBold, textTransform: "uppercase", color: T.mid, letterSpacing: ".04em", minWidth: 36, textAlign: "right" }}>Expand</span>
                </>
              ) : (
                <span style={{ fontSize: T.fsXs, fontWeight: T.fwBold, textTransform: "uppercase", color: T.mid, letterSpacing: ".04em", minWidth: 36, textAlign: "right" }}>Seats</span>
              )}
               {planEnabled && <span style={{ fontSize: T.fsXs, color: "var(--v2-light)", minWidth: 12, textAlign: "center" }}>→</span>}
            </div>
          </div>
          {visibleRows.map((row) => {
             if (isSeatsAvailable) return <SeatsAvailableRow key={row.key} row={row} meta={meta} onRowNavigate={onRowNavigate} planEnabled={planEnabled} />;
             if (meta.key === "needsSeats") return <NeedsSeatsRow key={row.key} row={row} meta={meta} onRowNavigate={onRowNavigate} planEnabled={planEnabled} />;
             if (meta.key === "comingOnline" && row.hasOffset) return <ComingOnlineRow key={row.key} row={row} meta={meta} onRowNavigate={onRowNavigate} planEnabled={planEnabled} />;
             return <StandardRow key={row.key} row={row} meta={meta} unit={column.unit} onRowNavigate={onRowNavigate} planEnabled={planEnabled} />;
          })}
          {hiddenCount > 0 && (
            <button
              data-testid={`v2-pipeline-viewall-${meta.testId}`}
              onClick={() => setExpanded((v) => !v)}
              style={{
                ...OUTBOUND_LINK_STYLE,
                marginLeft: "auto",
                padding: "0 2px",
                lineHeight: 1,
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                userSelect: "none",
                marginTop: 4,
              }}
            >
              <span>
                {expanded ? "Less" : "More"}
              </span>
              <span
                style={{
                  display: "inline-block",
                  transition: "transform 0.15s ease",
                  transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                }}
              >
                ▾
              </span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Main export ────────────────────────────────────────────────────────── */

export function SpaceHeadroomV2({ markets = [], onNavigate, planEnabled = true } = {}) {
  const columns = buildSpacePipelineColumns(markets);

  return (
    <div
      data-testid="v2-section-space-headroom"
      style={{
        background:   "var(--v2-card)",
        border:       "1px solid var(--v2-line)",
        borderRadius: 16,
        boxShadow:    "var(--v2-shadow-card)",
        padding:      "12px 20px",
        marginBottom: 16,
        minHeight:    PULSE_PANE_H,
      }}
    >
      {/* Header row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div data-testid="v2-space-pipeline-title" style={{ display: "flex", alignItems: "center", gap: 8, fontSize: T.fsBase, fontWeight: T.fwBold }}>
            <span style={{ display: "inline-block", width: 3, height: 14, background: "var(--v2-accent)", borderRadius: 2 }} />
            Space pipeline
            <PaneInfoTooltip text={SH_TOOLTIP} />
          </div>
        </div>

      </div>

      {/* Body: 3 equal columns */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16 }}>
        {COLUMN_META.map((meta) => (
          <PipelineColumn
            key={meta.key}
            meta={meta}
            column={columns[meta.key]}
            onRowNavigate={onNavigate}
            planEnabled={planEnabled}
          />
        ))}
      </div>

      {planEnabled && <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
        <button
          type="button"
          data-testid="v2-space-scenario-link"
          onClick={() => onNavigate?.("?view=whatif")}
          style={{
            ...OUTBOUND_LINK_STYLE,
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          Open space scenario <span aria-hidden="true">→</span>
        </button>
      </div>}
    </div>
  );
}
